/**
 * use-platform-affiliate.ts — Hooks Platform → Tiếp thị liên kết (super-admin).
 * Backend: ecdp_backend_source/src/modules/affiliate/affiliate-admin-routes.ts
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { platformApi } from '@/lib/platform-client'
import type { AffiliateConfig, CommissionItem, ExportedInfo, PartnerView, WithdrawalItem } from '@/lib/affiliate'

const KEY = ['platform', 'affiliate'] as const

export interface Paged<T> {
  items: T[]
  total: number
  page: number
  limit: number
}

export interface AffiliateOverview {
  partners: { active: number; suspended: number }
  clicks30d: number
  leadsNew: number
  referrals: number
  payingCustomers: number
  referredRevenue: number
  commission: { holding: number; available: number; void: number }
  withdrawals: {
    pending: { count: number; amount: number }
    approved: { count: number; amount: number }
    paid: { count: number; net: number; tax: number }
  }
}

export type AdminPartner = PartnerView & { orgId: string; orgName: string | null; referrals: number; earned: number }

export interface AdminReferral {
  id: string
  orgId: string
  orgName: string | null
  partnerId: string
  partnerCode: string
  partnerName: string
  source: string
  attributedAt: string
  lockedAt: string | null
  commissionUntil: string | null
  createdBy: string
  note: string | null
  revenue: number
  commission: number
}

export interface AdminLead {
  id: string
  fullName: string
  phone: string
  email: string | null
  companyName: string | null
  note: string | null
  status: 'new' | 'contacted' | 'converted' | 'rejected'
  orgId: string | null
  adminNote: string | null
  partnerCode: string
  partnerName: string
  createdAt: string
}

export type AdminCommission = CommissionItem & { orgId: string | null; partnerId: string; partnerCode: string; partnerName: string; createdBy: string }

export type AdminWithdrawal = WithdrawalItem & {
  partnerId: string
  partnerCode: string
  partnerName: string
  partnerType: string
  partnerStatus: string
  pitCommitment: boolean
  exportBatch: string | null
  payoutChangedRecently: boolean
}

export interface PartnerDetail {
  partner: PartnerView & { orgId: string; orgName: string | null; userId: string }
  summary: {
    clicks: number; clicks30d: number; leads: number; referrals: number; payingCustomers: number
    commission: { totalEarned: number; holding: number; available: number; pendingWithdrawal: number; paid: number }
  }
  referrals: Array<{ id: string; orgId: string; orgName: string | null; source: string; attributedAt: string; lockedAt: string | null; commissionUntil: string | null; createdBy: string; note: string | null }>
  commissions: CommissionItem[]
  withdrawals: WithdrawalItem[]
}

export function useAffiliateConfig() {
  return useQuery<{ config: AffiliateConfig; updatedAt: string | null; isDefault: boolean; effectiveVat: { includesVat: boolean; ratePercent: number } }>({
    queryKey: [...KEY, 'config'],
    queryFn: async () => (await platformApi.get('/platform/affiliate/config')).data,
  })
}

export function useAffiliateOverview() {
  return useQuery<AffiliateOverview>({
    queryKey: [...KEY, 'overview'],
    queryFn: async () => (await platformApi.get('/platform/affiliate/overview')).data,
  })
}

export function useAdminPartners(params: { q: string; status: string; page: number }) {
  return useQuery<Paged<AdminPartner>>({
    queryKey: [...KEY, 'partners', params],
    placeholderData: keepPreviousData,
    queryFn: async () => (await platformApi.get('/platform/affiliate/partners', { params: { ...params, status: params.status || undefined } })).data,
  })
}

export function usePartnerDetail(id: string | null) {
  return useQuery<PartnerDetail>({
    queryKey: [...KEY, 'partner', id],
    enabled: !!id,
    queryFn: async () => (await platformApi.get(`/platform/affiliate/partners/${id}`)).data,
  })
}

export function useAdminReferrals(page: number) {
  return useQuery<Paged<AdminReferral>>({
    queryKey: [...KEY, 'referrals', page],
    placeholderData: keepPreviousData,
    queryFn: async () => (await platformApi.get('/platform/affiliate/referrals', { params: { page } })).data,
  })
}

export function useAdminLeads(status: string, page: number) {
  return useQuery<Paged<AdminLead>>({
    queryKey: [...KEY, 'leads', status, page],
    placeholderData: keepPreviousData,
    queryFn: async () => (await platformApi.get('/platform/affiliate/leads', { params: { status: status || undefined, page } })).data,
  })
}

export function useAdminCommissions(params: { status: string; kind: string; partnerId: string; page: number }) {
  return useQuery<Paged<AdminCommission> & { sumAmount: number }>({
    queryKey: [...KEY, 'commissions', params],
    placeholderData: keepPreviousData,
    queryFn: async () => (await platformApi.get('/platform/affiliate/commissions', {
      params: { status: params.status || undefined, kind: params.kind || undefined, partnerId: params.partnerId || undefined, page: params.page },
    })).data,
  })
}

export function useAdminWithdrawals(status: string, page: number) {
  return useQuery<Paged<AdminWithdrawal>>({
    queryKey: [...KEY, 'withdrawals', status, page],
    placeholderData: keepPreviousData,
    queryFn: async () => (await platformApi.get('/platform/affiliate/withdrawals', { params: { status: status || undefined, page } })).data,
  })
}

function useM<TIn, TOut>(fn: (input: TIn) => Promise<TOut>) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: KEY }) })
}

export const useSaveAffiliateConfig = () =>
  useM(async (config: AffiliateConfig) => (await platformApi.put('/platform/affiliate/config', { config })).data)
export const useSetPartnerStatus = () =>
  useM(async (i: { id: string; status: 'active' | 'suspended'; reason: string }) =>
    (await platformApi.post(`/platform/affiliate/partners/${i.id}/status`, { status: i.status, reason: i.reason })).data)
export async function revealPartner(id: string, reason: string): Promise<{ idNumber: string | null; bankAccount: string | null }> {
  return (await platformApi.post(`/platform/affiliate/partners/${id}/reveal`, { reason })).data
}
export const useBindReferral = () =>
  useM(async (i: { orgId: string; code: string; reason: string; force?: boolean }) => (await platformApi.post('/platform/affiliate/referrals', i)).data)
export const useUpdateLead = () =>
  useM(async (i: { id: string; status?: string; adminNote?: string }) =>
    (await platformApi.patch(`/platform/affiliate/leads/${i.id}`, { status: i.status, adminNote: i.adminNote })).data)
export const useVoidCommission = () =>
  useM(async (i: { id: string; reason: string }) => (await platformApi.post(`/platform/affiliate/commissions/${i.id}/void`, { reason: i.reason })).data)
export const useCreateAdjustment = () =>
  useM(async (i: { partnerId: string; amount: number; reason: string }) => (await platformApi.post('/platform/affiliate/adjustments', i)).data)
export const useApproveWithdrawal = () =>
  useM(async (id: string) => (await platformApi.post(`/platform/affiliate/withdrawals/${id}/approve`)).data)
export const useRejectWithdrawal = () =>
  useM(async (i: { id: string; reason: string }) => (await platformApi.post(`/platform/affiliate/withdrawals/${i.id}/reject`, { reason: i.reason })).data)
export const useMarkWithdrawalPaid = () =>
  useM(async (i: { id: string; transferRef: string; paidAt?: string }) =>
    (await platformApi.post(`/platform/affiliate/withdrawals/${i.id}/paid`, { transferRef: i.transferRef, paidAt: i.paidAt })).data)
export const useReconcileAffiliate = () =>
  useM(async () => (await platformApi.post<{ released: number; created: number }>('/platform/affiliate/reconcile')).data)

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** Lỗi 409 "đã xuất ở lô X" (E17) — kèm danh sách để hỏi xác nhận xuất lại. */
export class AlreadyExportedError extends Error {
  constructor(message: string, public exported: ExportedInfo[]) {
    super(message)
  }
}

/**
 * Tải file CSV chuyển khoản cho các lệnh đã duyệt (ids rỗng = tất cả lệnh CHƯA xuất). Lệnh đã xuất → AlreadyExportedError
 * (gọi lại với force=true sau khi Platform xác nhận). Lệnh của đối tác bị khoá bị bỏ qua (E1).
 */
export async function exportWithdrawals(ids: string[], force = false): Promise<{ batch: string; skippedSuspended: number; reExported: number }> {
  try {
    const res = await platformApi.post('/platform/affiliate/withdrawals/export', { ids, force }, { responseType: 'blob' })
    const batch = String(res.headers['x-export-batch'] ?? 'lo')
    downloadBlob(res.data as Blob, `chi-hoa-hong-${batch}.csv`)
    return {
      batch,
      skippedSuspended: Number(res.headers['x-export-skipped-suspended'] ?? 0) || 0,
      reExported: Number(res.headers['x-export-reexported'] ?? 0) || 0,
    }
  } catch (e) {
    const r = (e as { response?: { status?: number; data?: unknown } }).response
    if (r?.data instanceof Blob) {
      let body: { error?: string; code?: string; exported?: ExportedInfo[] } = {}
      try { body = JSON.parse(await r.data.text()) } catch { /* không phải JSON */ }
      if (r.status === 409 && body.code === 'ALREADY_EXPORTED') throw new AlreadyExportedError(body.error ?? 'Lệnh đã xuất', body.exported ?? [])
      if (body.error) throw new Error(body.error)
    }
    throw e
  }
}

export interface TaxReportRow {
  partnerId: string
  partnerCode: string
  recipientName: string
  partnerType: string
  idOrTaxCode: string
  payouts: number
  withheldPayouts: number
  gross: number
  taxWithheld: number
  net: number
}

export interface TaxReport {
  period: { label: string; slug: string }
  rows: TaxReportRow[]
  totals: { recipients: number; payouts: number; gross: number; taxWithheld: number; net: number }
}

export function useTaxReport(params: Record<string, string>) {
  return useQuery<TaxReport>({
    queryKey: [...KEY, 'tax-report', params],
    placeholderData: keepPreviousData,
    queryFn: async () => (await platformApi.get('/platform/affiliate/tax-report', { params })).data,
  })
}

/** CSV kê khai (số CCCD / MST đầy đủ — Platform ghi nhật ký). */
export async function downloadTaxReportCsv(params: Record<string, string>, slug: string): Promise<void> {
  const res = await platformApi.get('/platform/affiliate/tax-report', { params: { ...params, format: 'csv' }, responseType: 'blob' })
  downloadBlob(res.data as Blob, `thue-tncn-hoa-hong-${slug}.csv`)
}

export async function loadPartnerTaxCertificate(partnerId: string, params: Record<string, string>): Promise<string> {
  return (await platformApi.get<string>(`/platform/affiliate/partners/${partnerId}/tax-certificate`, { params, responseType: 'text' })).data
}
